import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { TrophyIcon, CrownIcon, MedalIcon, RocketLaunchIcon, WarningIcon } from "@phosphor-icons/react";
import BotName from "@/components/BotName";


const RANK_META = (rank) => {
  if (rank === 1) return { Icon: CrownIcon,  color: "#FFD700", label: "GOLD"   };
  if (rank === 2) return { Icon: MedalIcon,  color: "#C0C0C0", label: "SILVER" };
  if (rank === 3) return { Icon: MedalIcon,  color: "#CD7F32", label: "BRONZE" };
  return { Icon: TrophyIcon, color: "#64748B", label: `#${rank}` };
};

export default function HallOfFame() {
  const [data, setData] = useState(null);
  const [limit, setLimit] = useState(10);
  const [deploying, setDeploying] = useState(false);
  const [deployMsg, setDeployMsg] = useState(null);

  useEffect(() => {
    api.get(`/hall-of-fame?limit=${limit}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ count: 0, bots: [] }));
  }, [limit]);

  const deployTop3 = async () => {
    setDeploying(true);
    setDeployMsg(null);
    try {
      const r = await api.post("/live/deploy", { limit: 3, virtual_balance: 1000 });
      setDeployMsg({
        tone: "ok",
        text: `Deployed ${r.data.deployed} new · restarted ${r.data.restarted}. See Live Trading page.`,
      });
    } catch (e) {
      setDeployMsg({ tone: "err", text: e?.response?.data?.detail || "Deploy failed" });
    } finally {
      setDeploying(false);
    }
  };

  if (!data) {
    return <div className="p-6 text-[#64748B] mono">loading…</div>;
  }

  return (
    <div data-testid="hall-of-fame-page" className="px-4 sm:px-6 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Control Room</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight">Hall of Fame</h1>
          <p className="text-[#94A3B8] text-sm mt-1 max-w-2xl">
            Top performers across the entire fleet, ranked by a composite score
            (growth × consistency ÷ drawdown, sample-weighted). Independent of
            status tier — a bot can be Champion, Elite, or Testing and still
            make the list.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/live"
            data-testid="hof-live-desk-link"
            className="px-3 py-1.5 text-xs mono uppercase tracking-wider border border-[#2A2D3A] text-[#94A3B8] hover:border-[#D6FF38]/40 hover:text-[#D6FF38] rounded transition-colors"
          >
            Live Desk →
          </Link>
          <button
            data-testid="hof-deploy-top3-btn"
            onClick={deployTop3}
            disabled={deploying || data.count === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs mono uppercase tracking-wider bg-[#D6FF38] text-[#0B0C10] hover:bg-[#c4ef2c] rounded disabled:opacity-50 transition-colors"
          >
            <RocketLaunchIcon size={12} weight="fill" />
            {deploying ? "Deploying…" : "Deploy Top 3"}
          </button>
          {[10, 20, 50].map((n) => (
            <button
              key={n}
              data-testid={`hof-limit-${n}`}
              onClick={() => setLimit(n)}
              className={`px-3 py-1.5 text-xs mono uppercase tracking-wider border ${
                limit === n
                  ? "border-[#D6FF38] text-[#D6FF38] bg-[#D6FF38]/5"
                  : "border-[#2A2D3A] text-[#64748B] hover:border-[#3F4250]"
              } rounded`}
            >
              Top {n}
            </button>
          ))}
        </div>
      </div>

      {deployMsg && (
        <div
          data-testid="hof-deploy-msg"
          className={`border rounded-md p-3 text-sm mono ${
            deployMsg.tone === "ok"
              ? "border-[#D6FF38]/30 bg-[#D6FF38]/5 text-[#D6FF38]"
              : "border-[#FF4D4D]/30 bg-[#FF4D4D]/5 text-[#FF4D4D]"
          }`}
        >
          {deployMsg.text}
        </div>
      )}

      {data.count === 0 ? (
        <div data-testid="hof-empty" className="border border-dashed border-[#2A2D3A] rounded-md p-12 text-center text-[#64748B] mono">
          No bots yet. Once your VPS bridge pushes data, the leaderboard will populate here.
        </div>
      ) : (
        <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[10px] mono uppercase tracking-widest text-[#64748B] border-b border-[#2A2D3A]">
              <tr>
                <th className="text-left px-3 sm:px-4 py-3 w-16">Rank</th>
                <th className="text-left px-3 sm:px-4 py-3">Bot</th>
                <th className="text-left px-3 sm:px-4 py-3 hidden sm:table-cell">Symbol</th>
                <th className="text-left px-3 sm:px-4 py-3 hidden md:table-cell">Status</th>
                <th className="text-right px-3 sm:px-4 py-3">Growth</th>
                <th className="text-right px-3 sm:px-4 py-3 hidden sm:table-cell">Win Mo.</th>
                <th className="text-right px-3 sm:px-4 py-3 hidden md:table-cell">DD</th>
                <th className="text-right px-3 sm:px-4 py-3 hidden lg:table-cell">Trades</th>
                <th className="text-right px-3 sm:px-4 py-3">Score</th>
              </tr>
            </thead>
            <tbody>
              {data.bots.map((b) => {
                const meta = RANK_META(b.rank);
                const Icon = meta.Icon;
                return (
                  <tr
                    key={b.id}
                    data-testid={`hof-row-${b.rank}`}
                    className="border-b border-[#1F2230] hover:bg-[#1A1D26] transition-colors"
                  >
                    <td className="px-3 sm:px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Icon size={18} weight="fill" style={{ color: meta.color }} />
                        <span className="mono text-xs" style={{ color: meta.color }}>{meta.label}</span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-3">
                      <Link to={`/bot/${b.id}`} className="mono text-white hover:text-[#D6FF38] transition-colors">
                        <BotName name={b.name} maxLen={40} />
                      </Link>
                      {b.suspicious && (
                        <span
                          title="Zero drawdown with high growth - data quality suspect"
                          className="inline-flex items-center gap-1 text-[9px] mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-[#FF4D4D]/40 bg-[#FF4D4D]/10 text-[#FF4D4D] ml-2"
                        >
                          <WarningIcon size={9} weight="fill" /> unverified
                        </span>
                      )}
                      <div className="text-[10px] mono text-[#64748B] sm:hidden">
                        {b.symbol} · {b.months_tested}mo
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-3 hidden sm:table-cell mono text-xs text-[#94A3B8]">
                      {b.symbol}
                    </td>
                    <td className="px-3 sm:px-4 py-3 hidden md:table-cell">
                      <StatusBadge status={b.status} />
                    </td>
                    <td className={`px-3 sm:px-4 py-3 mono text-right ${b.growth_pct >= 0 ? "text-[#2ECA7F]" : "text-[#FF4D4D]"}`}>
                      {b.growth_pct >= 0 ? "+" : ""}{fmt.num(b.growth_pct)}%
                    </td>
                    <td className="px-3 sm:px-4 py-3 hidden sm:table-cell mono text-right text-[#94A3B8]">
                      {fmt.num(b.profit_month_pct)}%
                    </td>
                    <td className="px-3 sm:px-4 py-3 hidden md:table-cell mono text-right text-[#94A3B8]">
                      {fmt.num(b.max_drawdown)}%
                    </td>
                    <td className="px-3 sm:px-4 py-3 hidden lg:table-cell mono text-right text-[#94A3B8]">
                      {b.total_trades || 0}
                    </td>
                    <td className="px-3 sm:px-4 py-3 mono text-right text-[#D6FF38]">
                      {fmt.num(b.score)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-[11px] mono text-[#64748B] text-center">
        Score = growth_pct × (profit_month_pct / 100) / max_drawdown × sample_weight ·
        ghost bots (0-DD) and low-trade bots get heavy penalty
      </div>
    </div>
  );
}
